// frontend/src/components/DisplayConditionBuilder.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { QuestionType, DisplayCondition, LogicOperator, getSelectableOptions } from "@/types/survey";
import { cn } from "@/lib/utils";

// Question types that have selectable options (answers stored as option IDs)
const CHOICE_QUESTION_TYPES = [
  "single_choice",
  "multiple_choice", 
  "dropdown",
  "yes_no",
  "likert_scale",
];

// Only radio_grid and checkbox_grid support row_equals (column selection semantics)
const ROW_EQUALS_SUPPORTED_TYPES = ["radio_grid", "checkbox_grid"];

// Helper: Check if a question type supports row_equals operator
const supportsRowEquals = (q: QuestionType): boolean => {
  return ROW_EQUALS_SUPPORTED_TYPES.includes(q.type);
};

// Helper to format grid value for display (ID-based only)
// Note: Only applies to radio_grid and checkbox_grid question types
const formatGridValue = (
  value: unknown,
  question: QuestionType | undefined
): string => {
  if (!question || typeof value !== "object" || value === null || Array.isArray(value)) {
    return String(value || "");
  }
  
  const gridValue = value as { rowId: string; columnId: string };
  const rows = question.rows || [];
  const columns = question.columns || [];
  
  // ID-based lookup only
  const rowLabel = rows.find(r => r.id === gridValue.rowId)?.label || "?";
  const colLabel = columns.find(c => c.id === gridValue.columnId)?.label || "?";
  
  return `${rowLabel} = ${colLabel}`;
};

interface DisplayConditionBuilderProps {
  question: QuestionType;
  allQuestions: QuestionType[];
  onChange: (conditions: DisplayCondition[]) => void;
}

export function DisplayConditionBuilder({ question, allQuestions, onChange }: DisplayConditionBuilderProps) {
  const [conditions, setConditions] = useState<DisplayCondition[]>(question.displayConditions || []);
  
  // Only allow referencing previous questions
  const previousQuestions = allQuestions.filter((q, i) => {
    const currentIndex = allQuestions.findIndex(aq => aq.id === question.id);
    return i < currentIndex;
  });
  
  // Helper: Check if a question type has selectable options
  // Include questions with allowOther even if no explicit options
  const isChoiceQuestion = (q: QuestionType): boolean => {
    const hasOptions = !!q.options && q.options.length > 0;
    const hasOther = !!q.allowOther;
    return CHOICE_QUESTION_TYPES.includes(q.type) && (hasOptions || hasOther);
  };
  
  // Helper: Get question by ID
  const getQuestionById = (questionId: string): QuestionType | undefined => {
    return allQuestions.find(q => q.id === questionId);
  };
  
  const updateConditions = (newConditions: DisplayCondition[]) => {
    setConditions(newConditions);
    onChange(newConditions);
  };
  
  const addCondition = () => {
    const newCondition: DisplayCondition = {
      conditions: [{
        questionId: previousQuestions[0]?.id || "",
        operator: "equals",
        value: "",
      }],
      conditionOperator: "AND",
      show: true,
    };
    updateConditions([...conditions, newCondition]);
  };
  
  const removeCondition = (index: number) => {
    updateConditions(conditions.filter((_, i) => i !== index));
  };
  
  const updateConditionGroup = (index: number, updates: Partial<DisplayCondition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    updateConditions(newConditions);
  };
  
  if (previousQuestions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        This is the first question - no display conditions can be set.
      </p>
    );
  }
  
  // Helper to truncate text
  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Render conditions for a group
  const renderConditionGroup = (group: DisplayCondition, groupIndex: number) => {
    return (
      <div className="space-y-2">
        {group.conditions.map((c, cIndex) => (
          <div key={cIndex} className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 rounded-lg">
            {/* Question Selector */}
            <Select
              value={c.questionId}
              onValueChange={(v) => {
                const newConds = [...group.conditions];
                newConds[cIndex] = { ...c, questionId: v };
                updateConditionGroup(groupIndex, { conditions: newConds });
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select question" />
              </SelectTrigger>
              <SelectContent>
                {previousQuestions.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {q.originalQuestionNumber || `Q${allQuestions.findIndex(aq => aq.id === q.id) + 1}`}: {truncate(q.question, 30)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Operator Selector */}
            <Select
              value={c.operator}
              onValueChange={(v) => {
                const newConds = [...group.conditions];
                newConds[cIndex] = { 
                  ...c, 
                  operator: v as LogicOperator,
                  value: (v === "row_equals" || v === "row_not_equals") 
                    ? { rowId: "", columnId: "" } 
                    : ""
                };
                updateConditionGroup(groupIndex, { conditions: newConds });
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const selectedQ = getQuestionById(c.questionId);
                  const isMatrixQuestion = selectedQ && supportsRowEquals(selectedQ);
                  
                  if (isMatrixQuestion) {
                    return (
                      <>
                        <SelectItem value="row_equals">row equals</SelectItem>
                        <SelectItem value="row_not_equals">row not equals</SelectItem>
                        <SelectItem value="is_not_empty">is answered</SelectItem>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <SelectItem value="equals">equals</SelectItem>
                        <SelectItem value="not_equals">does not equal</SelectItem>
                        <SelectItem value="contains">contains</SelectItem>
                        <SelectItem value="is_not_empty">is answered</SelectItem>
                      </>
                    );
                  }
                })()}
              </SelectContent>
            </Select>
            
            {/* Value Input (conditional) */}
            {!["is_empty", "is_not_empty"].includes(c.operator) && (() => {
              const selectedQuestion = getQuestionById(c.questionId);
              
              // Grid question with row_equals/row_not_equals
              if (selectedQuestion && supportsRowEquals(selectedQuestion) && 
                  (c.operator === "row_equals" || c.operator === "row_not_equals")) {
                const rows = selectedQuestion.rows || [];
                const columns = selectedQuestion.columns || [];
                const rawValue = (typeof c.value === "object" && c.value !== null && !Array.isArray(c.value))
                  ? c.value as { rowId?: string; columnId?: string }
                  : {};
                const currentRowId = rawValue.rowId || "";
                const currentColumnId = rawValue.columnId || "";
                
                return (
                  <div className="flex gap-2">
                    <Select
                      value={currentRowId}
                      onValueChange={(v) => {
                        const newConds = [...group.conditions];
                        newConds[cIndex] = { ...c, value: { rowId: v, columnId: currentColumnId } };
                        updateConditionGroup(groupIndex, { conditions: newConds });
                      }}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue placeholder="Row" />
                      </SelectTrigger>
                      <SelectContent>
                        {rows.map((row) => (
                          <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <span className="text-muted-foreground self-center">=</span>
                    
                    <Select
                      value={currentColumnId}
                      onValueChange={(v) => {
                        const newConds = [...group.conditions];
                        newConds[cIndex] = { ...c, value: { rowId: currentRowId, columnId: v } };
                        updateConditionGroup(groupIndex, { conditions: newConds });
                      }}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue placeholder="Column" />
                      </SelectTrigger>
                      <SelectContent>
                        {columns.map((col) => (
                          <SelectItem key={col.id} value={col.id}>{col.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              
              // Fallback for matrix value display
              if (typeof c.value === "object" && c.value !== null && 
                  !Array.isArray(c.value) && "rowId" in c.value && "columnId" in c.value) {
                return (
                  <span className="text-sm text-muted-foreground px-2 py-1 bg-muted rounded">
                    {formatGridValue(c.value, selectedQuestion)}
                  </span>
                );
              }
              
              // Choice question: dropdown of options
              if (selectedQuestion && isChoiceQuestion(selectedQuestion)) {
                const selectableOptions = getSelectableOptions(selectedQuestion);
                return (
                  <Select
                    value={String(c.value || "")}
                    onValueChange={(v) => {
                      const newConds = [...group.conditions];
                      newConds[cIndex] = { ...c, value: v };
                      updateConditionGroup(groupIndex, { conditions: newConds });
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select answer" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }
              
              // Default: Text input
              return (
                <Input
                  value={String(c.value || "")}
                  onChange={(e) => {
                    const newConds = [...group.conditions];
                    newConds[cIndex] = { ...c, value: e.target.value };
                    updateConditionGroup(groupIndex, { conditions: newConds });
                  }}
                  placeholder="Enter value"
                  className="w-[180px]"
                />
              );
            })()}
          </div>
        ))}
        
        {/* Add condition within group */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const newConds = [...group.conditions, {
              questionId: previousQuestions[0]?.id || "",
              operator: "equals" as LogicOperator,
              value: "",
            }];
            updateConditionGroup(groupIndex, { conditions: newConds });
          }}
        >
          <Plus className="h-4 w-4 mr-1" /> Add condition
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Display Conditions</Label>
      </div>
      
      {conditions.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No display conditions. This question will always be shown.
        </p>
      ) : (
        /* Condition Cards */
        <div className="space-y-3">
          {conditions.map((group, idx) => (
            <Card key={idx} className="border-border">
              <CardContent className="p-4">
                {/* Show/Hide for this condition */}
                <div className="flex items-center gap-2 mb-3">
                  <Badge 
                    variant={group.show ? "default" : "secondary"}
                    className={cn(
                      group.show 
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" 
                        : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                    )}
                  >
                    {group.show ? (
                      <><Eye className="h-3 w-3 mr-1" /> Show</>
                    ) : (
                      <><EyeOff className="h-3 w-3 mr-1" /> Hide</>
                    )}
                  </Badge>
                  <span className="text-sm text-muted-foreground">this question when:</span>
                  <div className="flex-1" />
                  <Switch
                    checked={group.show}
                    onCheckedChange={(checked) => updateConditionGroup(idx, { show: checked })}
                    aria-label="Toggle show/hide"
                  />
                </div>
                
                {/* Conditions */}
                {renderConditionGroup(group, idx)}
                
                {/* AND/OR Toggle */}
                {group.conditions.length > 1 && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">Match</span>
                    <Select 
                      value={group.conditionOperator} 
                      onValueChange={(v) => updateConditionGroup(idx, { conditionOperator: v as "AND" | "OR" })}
                    >
                      <SelectTrigger className="w-[100px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">All (AND)</SelectItem>
                        <SelectItem value="OR">Any (OR)</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">conditions</span>
                  </div>
                )}
              </CardContent>
              
              {/* Card Footer */}
              <div className="flex justify-end p-3 pt-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCondition(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Add Condition Button */}
      <Button variant="outline" onClick={addCondition} className="w-full border-dashed">
        <Plus className="h-4 w-4 mr-2" />
        Add Display Condition
      </Button>
    </div>
  );
}
